import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import cn from 'classnames';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

import { LandingApi } from 'src/api';
import { CONTACT_EMAIL } from 'src/constants/misc';
import { genLeadId, pushDataLayerEvent, splitName } from 'src/utils/helpers';

import Button from '../Button';
import { DoneIcon } from '../Icons';
import Input from '../Input';
import PhoneInput from '../PhoneInput';

import style from './ContactForm.module.scss';

const ZAPIER_WEBHOOK_URL = import.meta.env.VITE_ZAPIER_WEBHOOK_URL;
const FORM_TYPE = 'kontakt';

const EMPTY = { name: '', email: '', phone: '', company: '', message: '', country: 'PL' };

const RULES = {
  name: (values) => values.name.trim().length >= 2,
  email: (values) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim()),
  phone: (values) => isValidPhoneNumber(values.phone, values.country)
};

const isInvalid = (field, values) => !RULES[field](values);

// Same function as in kontakt.avalio.io landing page
function sendToZapier({ name, email, phone, company, message }) {
  if (!ZAPIER_WEBHOOK_URL) return;
  const { firstname, lastname } = splitName(name);
  fetch(ZAPIER_WEBHOOK_URL, {
    method: 'POST',
    mode: 'no-cors',
    body: new URLSearchParams({
      'Typ formularza': FORM_TYPE,
      Imię: firstname,
      Nazwisko: lastname,
      Email: email,
      'Numer telefonu': phone,
      'Nazwa kliniki': company,
      Pytanie: message,
      'Data wysłania': new Date().toISOString()
    })
  }).catch(() => {});
}

function pushFormSubmit({ name, email, phone }, leadId) {
  const { firstname, lastname } = splitName(name);
  pushDataLayerEvent('form_submit', {
    form_type: FORM_TYPE,
    firstname,
    lastname,
    email,
    phone,
    lead_id: leadId
  });
}

export default function ContactForm({ active, onSuccess, onClose }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const formRef = useRef(null);
  const doneButtonRef = useRef(null);

  useEffect(() => {
    if (status === 'done') doneButtonRef.current.focus({ preventScroll: true });
  }, [status]);

  const setValue = (field, value) => {
    const next = { ...values, [field]: value };
    const checked = field === 'country' ? 'phone' : field;
    setValues(next);
    // Live re-check only for fields already marked as invalid
    if (errors[checked]) setErrors((prev) => ({ ...prev, [checked]: isInvalid(checked, next) }));
  };

  const fieldProps = (field) => ({
    name: field,
    value: values[field],
    onChange: (e) => setValue(field, e.target.value),
    onBlur: () => RULES[field] && values[field] && setErrors((prev) => ({ ...prev, [field]: isInvalid(field, values) })),
    error: errors[field] ? t(`contactForm.errors.${field}`) : null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = Object.fromEntries(Object.keys(RULES).map((field) => [field, isInvalid(field, values)]));
    const firstInvalid = Object.keys(RULES).find((field) => nextErrors[field]);
    setErrors(nextErrors);
    if (firstInvalid) {
      formRef.current.elements.namedItem(firstInvalid).focus();
      return;
    }

    const data = { ...values, phone: parsePhoneNumberFromString(values.phone, values.country).number };

    try {
      setStatus('sending');
      const leadId = genLeadId();
      await LandingApi.contactUs({
        name: data.name,
        email: data.email,
        phone_number: data.phone,
        company: data.company,
        body: data.message
      });
      sendToZapier(data);
      pushFormSubmit(data, leadId);
      setStatus('done');
      onSuccess();
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className={style.done}>
        <span className={style.check}>
          <DoneIcon />
        </span>
        <h3>{t('contactForm.doneTitle')}</h3>
        <p>{t('contactForm.doneText')}</p>
        <Button
          ref={doneButtonRef}
          variant='ghost'
          onClick={onClose}
        >
          {t('contactForm.close')}
        </Button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className={cn(active && style.in)}
      noValidate
      onSubmit={handleSubmit}
    >
      <p className={style.k}>{t('contactForm.label')}</p>
      <div className={style.grid}>
        <Input
          id='dmName'
          label={t('contactForm.name')}
          autoComplete='name'
          required
          {...fieldProps('name')}
        />
        <Input
          id='dmEmail'
          type='email'
          label={t('contactForm.email')}
          autoComplete='email'
          required
          {...fieldProps('email')}
        />
        <PhoneInput
          id='dmPhone'
          label={t('contactForm.phone')}
          countryLabel={t('contactForm.countryCode')}
          country={values.country}
          onCountryChange={(country) => setValue('country', country)}
          required
          {...fieldProps('phone')}
        />
        <Input
          id='dmCompany'
          label={t('contactForm.company')}
          autoComplete='organization'
          {...fieldProps('company')}
        />
        <Input
          id='dmMsg'
          as='textarea'
          rows={4}
          wide
          label={t('contactForm.message')}
          placeholder={t('contactForm.messagePlaceholder')}
          {...fieldProps('message')}
        />
      </div>
      <div className={style.foot}>
        <p className={style.legal}>
          <Trans
            i18nKey='contactForm.legal'
            components={{
              link: (
                <Link
                  to='/privacy-policy'
                  onClick={onClose}
                />
              )
            }}
          />
        </p>
        <Button
          type='submit'
          size='lg'
          arrow
          disabled={status === 'sending'}
        >
          {status === 'sending' ? t('contactForm.sending') : t('contactForm.submit')}
        </Button>
      </div>
      {status === 'error' && (
        <p
          className={style.submitError}
          role='alert'
        >
          {t('contactForm.errors.submit', { email: CONTACT_EMAIL })}
        </p>
      )}
    </form>
  );
}
